import type { BotVariable } from '../../api/types.js';

/**
 * The response bodies a command or timer answers with, and the variable help.
 *
 * ==================== THE VARIABLE LIST IS THE SERVER'S ====================
 * `variables` comes from `GET /bot/variables`, which is the same allowlist the
 * API validates against. There is no list typed into the Console, for the
 * reason the chat composer holds no per-platform limits: a claim compiled into
 * a bundle is one nothing keeps current, and here it would be worse than
 * stale — an operator offered `{viewers}` would save a body the API refuses.
 * ==========================================================================
 */
export function BotResponseFields({
  responses,
  mode,
  variables,
  onChange,
}: {
  responses: string[];
  mode: 'static' | 'random';
  variables: BotVariable[];
  onChange: (responses: string[], mode: 'static' | 'random') => void;
}): React.JSX.Element {
  return (
    <div className="bot__responses">
      <div className="composer__field">
        <label className="field__label" htmlFor="response-mode">
          Reply with
        </label>
        <select
          id="response-mode"
          className="field__input"
          value={mode}
          onChange={(event) => {
            onChange(responses, event.target.value as 'static' | 'random');
          }}
        >
          <option value="static">The same message every time</option>
          <option value="random">One at random</option>
        </select>
      </div>

      {/*
        * Keyed by INDEX, which is safe here and would not be in general: these
        * rows have no identity of their own, and the only mutations are append
        * and remove-last. Nothing reorders them, so an index is stable for as
        * long as a row exists.
        */}
      {responses.map((response, index) => (
        <div className="composer__field" key={index}>
          <label className="field__label" htmlFor={`response-${String(index)}`}>
            {responses.length === 1 ? 'Response' : `Response ${String(index + 1)}`}
          </label>
          <textarea
            id={`response-${String(index)}`}
            className="field__input"
            rows={2}
            value={response}
            onChange={(event) => {
              const next = [...responses];
              next[index] = event.target.value;
              onChange(next, mode);
            }}
          />
        </div>
      ))}

      <div className="bot__actions">
        <button
          type="button"
          className="btn"
          onClick={() => {
            onChange([...responses, ''], mode);
          }}
        >
          Add another response
        </button>
        {responses.length > 1 && (
          <button
            type="button"
            className="btn"
            onClick={() => {
              onChange(responses.slice(0, -1), mode);
            }}
          >
            Remove last
          </button>
        )}
      </div>

      <VariableHelp variables={variables} />
    </div>
  );
}

/**
 * What a response may contain.
 *
 * Rendered from the server's list, and deliberately says what is NOT here: an
 * operator who expects `{viewers}` should learn it does not exist from this
 * panel rather than from a save that fails.
 */
export function VariableHelp({ variables }: { variables: BotVariable[] }): React.JSX.Element {
  if (variables.length === 0) return <></>;

  return (
    <details className="bot__help">
      <summary>Variables you can use</summary>
      <dl className="bot__variables">
        {variables.map((variable) => (
          <div key={variable.name}>
            <dt>
              <code>{`{${variable.name}}`}</code>
            </dt>
            <dd>{variable.description}</dd>
          </div>
        ))}
      </dl>
      <p className="field__hint">
        These are all of them. Connaro only offers variables it can fill in truthfully, so there is
        no viewer count, uptime or game — no platform reports those to Connaro per message.
      </p>
    </details>
  );
}
